import { z } from 'zod';
import { COUNTY_CATALOGUE_COUNT, SUPPORTED_STATES } from './counties/index.js';
import {
  ACTOR_TYPES,
  AUDIT_SOURCES,
  CALL_PROTOCOLS,
  DUPLICATE_MATCH_TYPES,
  EVIDENCE_TYPES,
  FOLLOW_UP_PRIORITIES,
  FOLLOW_UP_STATUSES,
  FOLLOW_UP_TYPES,
  HOSTING_STATUSES,
  IMPORT_FILE_KINDS,
  ISSUE_CONFIDENCES,
  ISSUE_SEVERITIES,
  ISSUE_STATUSES,
  NOTE_TYPES,
  PAYMENT_TYPES,
  PHONE_LINE_STATUSES,
  PIPELINE_STATUSES,
  PLACE_ID_CONFLICT_CHOICES,
  REPUTATION_METHODS,
  REPUTATION_SOURCES,
  SOURCE_TYPES,
  WEBSITE_CONDITIONS,
  WEBSITE_ISSUE_CATEGORIES,
  WEBSITE_PROJECT_STATUSES,
  WEB_PRESENCE_STATUSES,
  USER_ROLES,
  type UserRole,
} from './enums.js';

/** A local calendar date with no timezone attached: YYYY-MM-DD. */
export const localDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD calendar date');

/** A local wall-clock time with no timezone attached: HH:MM (24-hour). */
export const localTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a HH:MM 24-hour time');

const trimmedOptional = z
  .string()
  .trim()
  .max(2000)
  .optional()
  .nullable()
  .transform((v) => (v === '' || v === undefined ? null : v));

const urlOptional = z
  .string()
  .trim()
  .max(2048)
  .optional()
  .nullable()
  .transform((v) => (v === '' || v === undefined ? null : v));

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export const leadWriteSchema = z.object({
  businessName: z.string().trim().min(1, 'Business name is required').max(300),
  legalName: trimmedOptional,
  primaryCategoryId: z.number().int().positive().nullable().optional(),
  secondaryCategoryIds: z.array(z.number().int().positive()).max(20).optional(),
  ownerName: trimmedOptional,
  contactName: trimmedOptional,
  primaryPhone: trimmedOptional,
  secondaryPhone: trimmedOptional,
  email: trimmedOptional,
  streetAddress: trimmedOptional,
  city: trimmedOptional,
  countyId: z.number().int().positive().nullable().optional(),
  /** Counties the business SERVES. leads.county_id stays its physical location. */
  serviceCountyIds: z.array(z.number().int().positive()).max(COUNTY_CATALOGUE_COUNT).optional(),
  state: z.string().trim().max(2).nullable().optional(),
  zipCode: trimmedOptional,
  serviceAreas: z.array(z.string().trim().max(120)).max(50).optional(),
  noWebsiteConfirmed: z.boolean().optional(),
  googleBusinessProfileUrl: urlOptional,
  facebookUrl: urlOptional,
  bbbUrl: urlOptional,
  yelpUrl: urlOptional,
  googlePlaceId: trimmedOptional,
  rating: z.number().min(0).max(5).nullable().optional(),
  reviewCount: z.number().int().min(0).nullable().optional(),
  mostRecentReviewAt: z.number().int().nullable().optional(),
  outreachAngle: trimmedOptional,
  researchNotes: trimmedOptional,
  pipelineStatus: z.enum(PIPELINE_STATUSES).optional(),
  dateDiscovered: localDateSchema.nullable().optional(),
  businessStartedDate: localDateSchema.nullable().optional(),
  businessStartedPrecision: z.enum(["day", "month", "year"]).nullable().optional(),
});
export type LeadWriteInput = z.infer<typeof leadWriteSchema>;

export const leadPatchSchema = leadWriteSchema.partial();
export type LeadPatchInput = z.infer<typeof leadPatchSchema>;

export const dncSchema = z.object({
  doNotContact: z.boolean(),
  reason: z.string().trim().max(500).optional().nullable(),
});

export const pipelineStatusSchema = z.object({
  pipelineStatus: z.enum(PIPELINE_STATUSES),
  note: z.string().trim().max(1000).optional().nullable(),
});

// ---------------------------------------------------------------------------
// Lead filters — shared by list, export, queue building and saved filters
// ---------------------------------------------------------------------------

export const leadFilterSchema = z.object({
  opportunityTypes: z.array(z.enum(['new_website', 'website_improvement', 'unclassified', 'inconclusive'])).optional(),
  improvementCategories: z.array(z.enum(['search_visibility', 'form_functionality', 'performance_mobile', 'design_modernization'])).optional(),
  search: z.string().trim().max(200).optional(),
  pipelineStatuses: z.array(z.enum(PIPELINE_STATUSES)).optional(),
  categoryIds: z.array(z.number().int().positive()).optional(),
  countyIds: z.array(z.number().int().positive()).optional(),
  /** Counties the lead serves. Falls back to countyIds when countyMode needs it. */
  serviceCountyIds: z.array(z.number().int().positive()).optional(),
  /**
   * How the county filters are read.
   *   physical — leads.county_id only (the default, and what every saved filter
   *              written before service counties existed still means)
   *   served   — lead_service_counties only
   *   either   — located in OR serves
   */
  countyMode: z.enum(['physical', 'served', 'either']).optional(),
  /**
   * Physical state of the business. Validated against the states whose county
   * catalogue is supported, so a filter cannot name a state the app cannot
   * resolve counties for.
   */
  stateCodes: z.array(z.enum(SUPPORTED_STATES)).optional(),
  cities: z.array(z.string().trim().max(120)).optional(),
  webPresenceStatuses: z.array(z.enum(WEB_PRESENCE_STATUSES)).optional(),
  websiteConditions: z.array(z.enum(WEBSITE_CONDITIONS)).optional(),
  minRating: z.number().min(0).max(5).optional(),
  maxRating: z.number().min(0).max(5).optional(),
  minReviewCount: z.number().int().min(0).optional(),
  minScore: z.number().int().min(0).max(100).optional(),
  maxScore: z.number().int().min(0).max(100).optional(),
  hasOwnerName: z.boolean().optional(),
  /** The owner or contact name is backed by a BBB profile (verified_owner or role_confirmed_contact evidence). */
  ownerVerified: z.boolean().optional(),
  hasPhone: z.boolean().optional(),
  hasEmail: z.boolean().optional(),
  minAttemptCount: z.number().int().min(0).optional(),
  maxAttemptCount: z.number().int().min(0).optional(),
  lastAttemptBefore: localDateSchema.optional(),
  lastAttemptAfter: localDateSchema.optional(),
  importedAfter: localDateSchema.optional(),
  importedBefore: localDateSchema.optional(),
  importId: z.number().int().positive().optional(),
  followUpState: z.enum(['any', 'none', 'open', 'overdue', 'due_today']).optional(),
  cooldownEligibleOnly: z.boolean().optional(),
  /** Untouched leads only; calling queues enforce this even when omitted. */
  freshOnly: z.boolean().optional(),
  includeArchived: z.boolean().optional(),
  includeDnc: z.boolean().optional(),
  onlyDnc: z.boolean().optional(),
  onlyArchived: z.boolean().optional(),
  hasOpenWebsiteIssues: z.boolean().optional(),
  minOpenIssueSeverity: z.enum(ISSUE_SEVERITIES).optional(),
  websiteIssueTypeCodes: z.array(z.string().trim().max(80)).optional(),
  brokenFormOnly: z.boolean().optional(),
  indexingProblemOnly: z.boolean().optional(),
  mobileProblemOnly: z.boolean().optional(),
  hasCustomer: z.boolean().optional(),
  maxBusinessAgeMonths: z.number().int().min(1).max(600).optional(),
  hasBusinessStartDate: z.boolean().optional(),
});
export type LeadFilter = z.infer<typeof leadFilterSchema>;

export const LEAD_SORT_FIELDS = [
  'businessName',
  'city',
  'rating',
  'reviewCount',
  'pipelineStatus',
  'normalizedLeadScore',
  'lastContactedAt',
  'dateImported',
  'createdAt',
  'updatedAt',
  'businessStartedDate',
] as const;

export const leadListQuerySchema = z.object({
  filter: leadFilterSchema.optional().default({}),
  sortBy: z.enum(LEAD_SORT_FIELDS).optional().default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).optional().default('desc'),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});
export type LeadListQuery = z.infer<typeof leadListQuerySchema>;

export const savedFilterWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: trimmedOptional,
  filter: leadFilterSchema,
  sortBy: z.enum(LEAD_SORT_FIELDS).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
});

// ---------------------------------------------------------------------------
// Notes, activities
// ---------------------------------------------------------------------------

export const noteWriteSchema = z.object({
  noteBody: z.string().trim().min(1, 'Note cannot be empty').max(20000),
  noteType: z.enum(NOTE_TYPES).optional().default('General'),
});

// ---------------------------------------------------------------------------
// Categories / counties
// ---------------------------------------------------------------------------

export const categoryWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, digits and dashes')
    .max(120)
    .optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

export const countyWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  state: z.string().trim().length(2),
  /** 5-digit state+county FIPS code. Null for a hand-created county. */
  fipsCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'A FIPS code is five digits')
    .nullish(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

// ---------------------------------------------------------------------------
// Websites (Slice B)
// ---------------------------------------------------------------------------

export const websiteWriteSchema = z.object({
  url: z.string().trim().min(3).max(2048),
  isOfficial: z.boolean().optional().default(true),
  isPrimary: z.boolean().optional().default(false),
  condition: z.enum(WEBSITE_CONDITIONS).optional().default('Unknown'),
  finalResolvedUrl: urlOptional,
  httpStatus: z.number().int().min(0).max(999).nullable().optional(),
  redirectTarget: urlOptional,
  httpsEnabled: z.boolean().nullable().optional(),
  sslStatus: trimmedOptional,
  detectedPlatform: trimmedOptional,
  notes: trimmedOptional,
});

export const leadSourceWriteSchema = z.object({
  sourceType: z.enum(SOURCE_TYPES),
  sourceUrl: urlOptional,
  sourceTitle: trimmedOptional,
  sourceNotes: trimmedOptional,
  observedAt: z.number().int().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Imports (Slice B / E)
// ---------------------------------------------------------------------------

export const columnMappingSchema = z.object({
  /** Map of lead field name -> source column header. */
  fields: z.record(z.string(), z.string().nullable()),
  /** Constant values applied to every row, e.g. a county or source type. */
  constants: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});
export type ColumnMapping = z.infer<typeof columnMappingSchema>;

export const mappingTemplateWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  fileKind: z.enum(IMPORT_FILE_KINDS).optional().default('csv'),
  mapping: columnMappingSchema,
});

export const importCommitSchema = z.object({
  mapping: columnMappingSchema,
  saveTemplateName: z.string().trim().max(120).optional().nullable(),
  forceReimport: z.boolean().optional().default(false),
});

export const duplicateResolveSchema = z.object({
  action: z.enum(['approve_update', 'insert_anyway', 'skip', 'merge', 'keep_both', 'keep_both_suppress', 'separate_location']),
  /** [v5] Required when action is insert_anyway and the conflict involves a Place ID. */
  placeIdChoice: z.enum(PLACE_ID_CONFLICT_CHOICES).optional(),
  /** [v5] Field-level survivor selection for merges: field name -> 'source' | 'target'. */
  fieldChoices: z.record(z.string(), z.enum(['source', 'target'])).optional(),
  notes: trimmedOptional,
});
export type DuplicateResolveInput = z.infer<typeof duplicateResolveSchema>;

// ---------------------------------------------------------------------------
// Calling (Slice C)
// ---------------------------------------------------------------------------

export const queuePresetWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: trimmedOptional,
  filter: leadFilterSchema,
  orderBy: z
    .enum([
      'score_desc',
      'newest_business',
      'rating_desc',
      'review_count_desc',
      'oldest_attempt',
      'random',
      'created_desc',
    ])
    .optional()
    .default('created_desc'),
  maxLeads: z.number().int().min(1).max(5000).optional().default(200),
});
export type QueuePresetWrite = z.infer<typeof queuePresetWriteSchema>;

export const callSessionCreateSchema = z.object({
  presetId: z.number().int().positive().optional().nullable(),
  name: z.string().trim().max(160).optional().nullable(),
  filter: leadFilterSchema.optional(),
  orderBy: queuePresetWriteSchema.shape.orderBy.optional(),
  maxLeads: z.number().int().min(1).max(5000).optional(),
});

export const callAttemptWriteSchema = z.object({
  leadId: z.number().int().positive(),
  phoneNumber: z.string().trim().max(40),
  outcomeId: z.number().int().positive(),
  startedAt: z.number().int().optional(),
  endedAt: z.number().int().optional(),
  approximateDurationSeconds: z.number().int().min(0).max(86400).optional().nullable(),
  notes: trimmedOptional,
  followUpRequired: z.boolean().optional().default(false),
  sessionId: z.number().int().positive().optional().nullable(),
  /** Optional follow-up created together with the outcome. */
  followUp: z
    .object({
      followUpType: z.enum(FOLLOW_UP_TYPES),
      dueDate: localDateSchema,
      dueTime: localTimeSchema.nullable().optional(),
      priority: z.enum(FOLLOW_UP_PRIORITIES).optional().default('Normal'),
      notes: trimmedOptional,
    })
    .optional()
    .nullable(),
});

export const callOutcomeWriteSchema = z.object({
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
  countsAsContact: z.boolean().optional(),
});

export const outcomeMappingWriteSchema = z.object({
  outcomeId: z.number().int().positive(),
  setPipelineStatus: z.enum(PIPELINE_STATUSES).nullable().optional(),
  applyDnc: z.boolean().optional(),
  dncReason: trimmedOptional,
  triggersCustomerConversion: z.boolean().optional(),
  suggestFollowUpType: z.enum(FOLLOW_UP_TYPES).nullable().optional(),
  suggestFollowUpDays: z.number().int().min(0).max(365).nullable().optional(),
  isActive: z.boolean().optional(),
});

export const cooldownRuleWriteSchema = z.object({
  outcomeId: z.number().int().positive(),
  durationMinutes: z.number().int().min(0).max(525600).nullable().optional(),
  useFollowUpDate: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
});

// ---------------------------------------------------------------------------
// Follow-ups (Slice D)
// ---------------------------------------------------------------------------

export const followUpWriteSchema = z.object({
  leadId: z.number().int().positive(),
  followUpType: z.enum(FOLLOW_UP_TYPES),
  dueDate: localDateSchema,
  dueTime: localTimeSchema.nullable().optional(),
  timezone: z.string().trim().max(80).optional(),
  priority: z.enum(FOLLOW_UP_PRIORITIES).optional().default('Normal'),
  notes: trimmedOptional,
});

export const followUpPatchSchema = followUpWriteSchema.partial().omit({ leadId: true }).extend({
  status: z.enum(FOLLOW_UP_STATUSES).optional(),
});

export const followUpSnoozeSchema = z.object({
  snoozedUntilDate: localDateSchema,
  snoozedUntilTime: localTimeSchema.nullable().optional(),
});

export const followUpCompleteSchema = z.object({
  notes: trimmedOptional,
  createAnother: z
    .object({
      followUpType: z.enum(FOLLOW_UP_TYPES),
      dueDate: localDateSchema,
      dueTime: localTimeSchema.nullable().optional(),
      priority: z.enum(FOLLOW_UP_PRIORITIES).optional(),
      notes: trimmedOptional,
    })
    .optional()
    .nullable(),
});

// ---------------------------------------------------------------------------
// Customers and payments (Slice D / F)
// ---------------------------------------------------------------------------

export const customerWriteSchema = z.object({
  leadId: z.number().int().positive(),
  serviceSold: z.string().trim().min(1).max(200),
  saleAmountCents: z.number().int().min(0).max(1_000_000_00),
  saleDate: localDateSchema,
  domainName: trimmedOptional,
  websiteProjectStatus: z.enum(WEBSITE_PROJECT_STATUSES).optional().default('Not Started'),
  hostingStatus: z.enum(HOSTING_STATUSES).optional().default('None'),
  renewalDate: localDateSchema.nullable().optional(),
  customerNotes: trimmedOptional,
});

export const customerPatchSchema = customerWriteSchema.partial().omit({ leadId: true });

export const paymentWriteSchema = z.object({
  amountCents: z.number().int().refine((v) => v !== 0, 'Payment amount cannot be zero'),
  paymentDate: localDateSchema,
  paymentType: z.enum(PAYMENT_TYPES),
  notes: trimmedOptional,
});

// ---------------------------------------------------------------------------
// Website audits and issues (Slice E)
// ---------------------------------------------------------------------------

export const websiteIssueWriteSchema = z.object({
  issueTypeCode: z.string().trim().min(1).max(80),
  title: trimmedOptional,
  description: trimmedOptional,
  severity: z.enum(ISSUE_SEVERITIES).optional(),
  confidence: z.enum(ISSUE_CONFIDENCES).optional(),
  affectedUrl: urlOptional,
  evidenceType: z.enum(EVIDENCE_TYPES).optional().default('none'),
  evidenceValue: trimmedOptional,
});

export const manualAuditWriteSchema = z.object({
  websiteId: z.number().int().positive(),
  auditedAt: z.number().int().optional(),
  summary: trimmedOptional,
  pagesChecked: z.number().int().min(0).max(100000).nullable().optional(),
  formsChecked: z.number().int().min(0).max(100000).nullable().optional(),
  linksChecked: z.number().int().min(0).max(1000000).nullable().optional(),
  overallHealthScore: z.number().int().min(0).max(100).nullable().optional(),
  issues: z.array(websiteIssueWriteSchema).max(500).optional().default([]),
});

export const issueResolveSchema = z.object({
  issueStatus: z.enum(ISSUE_STATUSES),
  resolutionNotes: trimmedOptional,
});

export const issueTypeWriteSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9_]+$/, 'Codes are lowercase with underscores')
    .max(80),
  label: z.string().trim().min(1).max(160),
  category: z.enum(WEBSITE_ISSUE_CATEGORIES),
  defaultSeverity: z.enum(ISSUE_SEVERITIES),
  defaultConfidence: z.enum(ISSUE_CONFIDENCES),
  scoreWeight: z.number().min(0).max(100),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

// ---------------------------------------------------------------------------
// Structured JSON / JSONL import payload (Slice E)
// ---------------------------------------------------------------------------

/**
 * The nested shapes are named exports so unknown-field detection can read its
 * list of valid keys straight off `.shape`. A second hand-written key list would
 * drift the moment a field was added here.
 */
export const importIssueSchema = z.object({
  code: z.string().trim().max(80),
  title: z.string().trim().max(300).nullish(),
  description: z.string().trim().max(5000).nullish(),
  severity: z.enum(ISSUE_SEVERITIES).nullish(),
  confidence: z.enum(ISSUE_CONFIDENCES).nullish(),
  affectedUrl: z.string().trim().max(2048).nullish(),
  evidenceType: z.enum(EVIDENCE_TYPES).nullish(),
  evidenceValue: z.string().trim().max(20000).nullish(),
  issueStatus: z.enum(ISSUE_STATUSES).nullish(),
});

export const importAuditSchema = z.object({
  auditSource: z.enum(AUDIT_SOURCES).nullish(),
  auditVersion: z.string().trim().max(60).nullish(),
  auditedAt: z.union([z.number().int(), z.string()]).nullish(),
  pagesChecked: z.number().int().nullish(),
  formsChecked: z.number().int().nullish(),
  linksChecked: z.number().int().nullish(),
  overallHealthScore: z.number().int().min(0).max(100).nullish(),
  /** Accepted for compatibility; the stored value is derived from the issues. */
  overallSeverity: z.enum(ISSUE_SEVERITIES).nullish(),
  summary: z.string().trim().max(5000).nullish(),
  rawEvidence: z.unknown().nullish(),
  issues: z.array(importIssueSchema).max(500).nullish(),
});

export const importWebsiteSchema = z.object({
  url: z.string().trim().min(3).max(2048),
  isOfficial: z.boolean().nullish(),
  isPrimary: z.boolean().nullish(),
  condition: z.string().trim().max(60).nullish(),
  finalResolvedUrl: z.string().trim().max(2048).nullish(),
  httpStatus: z.number().int().nullish(),
  redirectTarget: z.string().trim().max(2048).nullish(),
  httpsEnabled: z.boolean().nullish(),
  sslStatus: z.string().trim().max(120).nullish(),
  detectedPlatform: z.string().trim().max(120).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  audits: z.array(importAuditSchema).max(50).nullish(),
});

export const importSourceSchema = z.object({
  sourceType: z.string().trim().max(60).nullish(),
  sourceUrl: z.string().trim().max(2048).nullish(),
  sourceTitle: z.string().trim().max(300).nullish(),
  sourceNotes: z.string().trim().max(2000).nullish(),
  observedAt: z.union([z.number().int(), z.string()]).nullish(),
});

export const structuredLeadImportSchema = z.object({
  businessName: z.string().trim().min(1).max(300),
  legalName: z.string().trim().max(300).nullish(),
  ownerName: z.string().trim().max(200).nullish(),
  contactName: z.string().trim().max(200).nullish(),
  primaryPhone: z.string().trim().max(60).nullish(),
  secondaryPhone: z.string().trim().max(60).nullish(),
  email: z.string().trim().max(200).nullish(),
  streetAddress: z.string().trim().max(300).nullish(),
  city: z.string().trim().max(120).nullish(),
  county: z.string().trim().max(120).nullish(),
  state: z.string().trim().max(2).nullish(),
  /**
   * Disambiguates `county` when the name exists in more than one state.
   *
   * Constrained to the states whose county catalogue is supported rather than
   * to any two letters: a file saying "IN" cannot have its county resolved, so
   * accepting the value would only produce a lead with a state we cannot check
   * a county against.
   *
   * These two MUST be declared here. zod strips unknown keys, so a field that
   * is documented but not declared is silently discarded before the mapper ever
   * sees it — which is exactly how this feature shipped not working.
   */
  stateCode: z.enum(SUPPORTED_STATES).nullish(),
  /** 5-digit Census county code. The unambiguous way to name a county. */
  countyFips: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'countyFips must be exactly 5 digits')
    .nullish(),
  zipCode: z.string().trim().max(20).nullish(),
  googlePlaceId: z.string().trim().max(200).nullish(),
  googleBusinessProfileUrl: z.string().trim().max(2048).nullish(),
  facebookUrl: z.string().trim().max(2048).nullish(),
  bbbUrl: z.string().trim().max(2048).nullish(),
  yelpUrl: z.string().trim().max(2048).nullish(),
  rating: z.number().min(0).max(5).nullish(),
  reviewCount: z.number().int().min(0).nullish(),
  /** Epoch seconds or any date string toEpochSeconds can read. */
  mostRecentReviewAt: z.union([z.number().int(), z.string()]).nullish(),
  /** Date string; stored as a local calendar date. */
  dateDiscovered: z.string().trim().max(40).nullish(),
  outreachAngle: z.string().trim().max(2000).nullish(),
  researchNotes: z.string().trim().max(20000).nullish(),
  noWebsiteConfirmed: z.boolean().nullish(),
  businessStartedDate: z.string().trim().max(30).nullish(),
  /**
   * Declared precision. Advisory only: the precision actually stored is
   * inferred from the date format, and a contradiction produces a warning.
   * See mapStructuredRecord.
   */
  businessStartedPrecision: z.enum(['day', 'month', 'year']).nullish(),
  primaryCategory: z.string().trim().max(120).nullish(),
  categories: z.array(z.string().trim().max(120)).max(20).nullish(),
  /** Free-text towns and areas. Not counties — see serviceCounties. */
  serviceAreas: z.array(z.string().trim().max(120)).max(50).nullish(),
  /** Counties served, by name. Matched against the county catalogue. */
  serviceCounties: z.array(z.string().trim().max(120)).max(COUNTY_CATALOGUE_COUNT).nullish(),
  sources: z.array(importSourceSchema).max(50).nullish(),
  websites: z.array(importWebsiteSchema).max(20).nullish(),
});
export type StructuredLeadImport = z.infer<typeof structuredLeadImportSchema>;

/**
 * Valid key names per nesting level, read off the schemas above so the two can
 * never disagree. Used by collectUnknownFields to warn about a misspelled or
 * unsupported field instead of silently dropping it (zod strips by default).
 */
export const STRUCTURED_IMPORT_KEYS = {
  root: Object.keys(structuredLeadImportSchema.shape),
  sources: Object.keys(importSourceSchema.shape),
  websites: Object.keys(importWebsiteSchema.shape),
  audits: Object.keys(importAuditSchema.shape),
  issues: Object.keys(importIssueSchema.shape),
} as const;

// ---------------------------------------------------------------------------
// Settings / backups (Slice F)
// ---------------------------------------------------------------------------

export const settingWriteSchema = z.object({
  key: z.string().trim().min(1).max(120),
  value: z.unknown(),
});

export const scoringConfigSchema = z.object({
  version: z.number().int().min(1),
  weights: z.object({
    noWebsite: z.number(),
    websiteConditionParked: z.number(),
    websiteConditionExpired: z.number(),
    websiteConditionUnreachable: z.number(),
    websiteConditionOutdated: z.number(),
    websiteConditionWeak: z.number(),
    websiteConditionFunctioning: z.number(),
    facebookOnly: z.number(),
    googleProfileOnly: z.number(),
    directoryOnly: z.number(),
    ratingBelowThreshold: z.number(),
    highReviewCount: z.number(),
    recentReviewActivity: z.number(),
    noRecentActivity: z.number(),
    hasOwnerName: z.number(),
    hasPhone: z.number(),
    businessUnder1Year: z.number(),
    businessUnder2Years: z.number(),
    businessUnder4Years: z.number(),
    businessOver4Years: z.number(),
  }),
  issueStrategy: z.object({
    maxIssuesCounted: z.number().int().min(1).max(50),
    perCategoryCap: z.number().min(0),
    alwaysIncludeHighest: z.boolean(),
  }),
  thresholds: z.object({
    recentReviewMonths: z.number().int().min(1).max(120),
    inactiveMonths: z.number().int().min(1).max(240),
    lowRating: z.number().min(0).max(5),
    highReviewCount: z.number().int().min(1),
    youngBusinessMonths: z.number().int().min(1).max(240),
    establishingBusinessMonths: z.number().int().min(1).max(240),
    matureBusinessMonths: z.number().int().min(1).max(600),
  }),
  normalization: z.object({
    rawMin: z.number(),
    rawMax: z.number(),
  }),
});
export type ScoringConfig = z.infer<typeof scoringConfigSchema>;

export const callSettingsSchema = z.object({
  protocol: z.enum(CALL_PROTOCOLS),
  sipDomain: z.string().trim().max(200).optional().nullable(),
});

export const backupSettingsSchema = z.object({
  destination: z.string().trim().min(1).max(500),
  retentionCount: z.number().int().min(1).max(500),
  scheduledEnabled: z.boolean().default(true),
  scheduledIntervalHours: z.number().int().min(1).max(168).default(24),
});

export const importSettingsSchema = z.object({
  watchedFolderEnabled: z.boolean(),
  watchedFolderRoot: z.string().trim().min(1).max(500),
  rawRowRetentionDays: z.number().int().min(0).max(3650),
});

export const duplicateSettingsSchema = z.object({
  fuzzyAutoMergeThreshold: z.number().min(0).max(1),
  fuzzyReviewThreshold: z.number().min(0).max(1),
  maxCandidatesPerLead: z.number().int().min(1).max(200),
  /**
   * Import-time "same normalized name in the same city" rule. Candidate-only:
   * it can never auto-update or auto-merge. Defaults on because measuring it
   * against the live database produced zero candidates on existing data.
   */
  nameCityCandidateEnabled: z.boolean(),
});

export const restoreRequestSchema = z.object({
  backupFile: z.string().trim().min(1).max(500),
  confirm: z.literal(true),
});

export const auditLogQuerySchema = z.object({
  actorType: z.enum(ACTOR_TYPES).optional(),
  action: z.string().trim().max(120).optional(),
  entityType: z.string().trim().max(80).optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(200).optional().default(50),
});

export const reportRangeSchema = z.object({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
});

export const duplicateListQuerySchema = z.object({
  state: z.enum(['pending', 'resolved', 'all']).optional().default('pending'),
  matchTypes: z.array(z.enum(DUPLICATE_MATCH_TYPES)).optional(),
  minConfidence: z.number().min(0).max(1).optional(),
  sortBy: z.enum(['confidence', 'created']).optional().default('confidence'),
  sortDir: z.enum(['asc', 'desc']).optional().default('desc'),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(200).optional().default(25),
});

export const bulkDuplicateActionSchema = z.object({
  action: z.enum(['merge_safe_phone', 'reject', 'keep_both_suppress']),
  candidateIds: z.array(z.number().int().positive()).max(5000).optional(),
  matchTypes: z.array(z.enum(DUPLICATE_MATCH_TYPES)).optional(),
});

// ---------------------------------------------------------------------------
// Phase 1 — authentication
// ---------------------------------------------------------------------------

/**
 * Twelve characters, and no composition rules. Length is the only requirement
 * that reliably increases work for an attacker; "one capital, one symbol"
 * mostly produces Password1! and a sticky note.
 */
export const MIN_PASSWORD_LENGTH = 12;

export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
  .max(200);

export const usernameSchema = z.string().trim().min(1, 'Username is required').max(80);

export const loginSchema = z.object({
  username: usernameSchema,
  // Not passwordSchema: an existing password is checked against the stored
  // hash, never re-validated. A length rule here would leak policy changes.
  password: z.string().min(1, 'Password is required').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required').max(200),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const userCreateSchema = z.object({
  username: usernameSchema,
  displayName: z.string().trim().min(1, 'Display name is required').max(120),
  role: z.enum(USER_ROLES),
  password: passwordSchema,
  mustChangePassword: z.boolean().optional(),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

/** A user as the API returns it. No hash, no lockout internals. */
export interface PublicUser {
  id: number;
  username: string;
  displayName: string;
  role: UserRole;
  isActive: boolean;
  mustChangePassword: boolean;
  agentId: number | null;
  lastLoginAt: number | null;
}

export interface AuthState {
  /** Whether the server enforces authentication. False for all of Phase 1. */
  authEnabled: boolean;
  user: PublicUser | null;
}

// ---------------------------------------------------------------------------
// Phase 5 — phone lines
// ---------------------------------------------------------------------------

/**
 * A US phone number in E.164, or null while the number is mid-port.
 *
 * Ten digits plus the country code. Deliberately NOT restricted to any area
 * code: the owner's first numbers will probably be 937, but a business rule
 * that says so would have to be unpicked the first time a Michigan number is
 * bought, and there is no security or correctness reason for it.
 */
export const e164UsSchema = z
  .string()
  .trim()
  .regex(/^\+1[2-9]\d{9}$/, 'Expected a US number in E.164 form, e.g. +19375550123');

/**
 * Turn what a human types into E.164, leaving anything unrecognisable alone so
 * the validator can report it rather than silently mangling it.
 *
 * Exported because both the schema and the phone-line service normalise: the
 * service must not depend on having been called through a parsed route, or a
 * CLI or a test writes a differently-shaped number into the same column.
 */
export function toE164Us(value: string): string {
  const digits = value.trim().replace(/\D+/g, '');
  // A leading 1 is the country code, not an area code.
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10 ? `+1${national}` : value.trim();
}

/** Accepts what a human types and normalises it, or refuses clearly. */
export const phoneLineNumberSchema = z.string().trim().transform(toE164Us).pipe(e164UsSchema);

const lineLabelSchema = z.string().trim().min(1, 'A label is required').max(80);
const lineNotesSchema = z.string().trim().max(2000).nullable().optional();

export const phoneLineCreateSchema = z.object({
  label: lineLabelSchema,
  e164: phoneLineNumberSchema.nullable().optional(),
  protocol: z.enum(CALL_PROTOCOLS).optional(),
  sipDomain: z.string().trim().max(200).nullable().optional(),
  sipUsername: z.string().trim().max(200).nullable().optional(),
  defaultAgentId: z.number().int().positive().nullable().optional(),
  provider: z.string().trim().max(80).nullable().optional(),
  providerLineId: z.string().trim().max(120).nullable().optional(),
  dailyCallCap: z.number().int().min(1).max(10_000).nullable().optional(),
  notes: lineNotesSchema,
  /**
   * Where the line starts. Defaults to 'warming' and cannot be 'retired': a
   * line created already dead is a data-entry mistake, not a lifecycle.
   */
  status: z.enum(PHONE_LINE_STATUSES).exclude(['retired']).optional(),
});
export type PhoneLineCreateInput = z.infer<typeof phoneLineCreateSchema>;

/**
 * Metadata only. Status is deliberately absent — it moves through the guarded
 * transition endpoint, which writes an event row. Letting a PATCH change it
 * would let lifecycle history be edited into existence with no record.
 */
export const phoneLineUpdateSchema = z.object({
  label: lineLabelSchema.optional(),
  e164: phoneLineNumberSchema.nullable().optional(),
  protocol: z.enum(CALL_PROTOCOLS).optional(),
  sipDomain: z.string().trim().max(200).nullable().optional(),
  sipUsername: z.string().trim().max(200).nullable().optional(),
  defaultAgentId: z.number().int().positive().nullable().optional(),
  provider: z.string().trim().max(80).nullable().optional(),
  providerLineId: z.string().trim().max(120).nullable().optional(),
  dailyCallCap: z.number().int().min(1).max(10_000).nullable().optional(),
  notes: lineNotesSchema,
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type PhoneLineUpdateInput = z.infer<typeof phoneLineUpdateSchema>;

export const phoneLineStatusChangeSchema = z.object({
  status: z.enum(PHONE_LINE_STATUSES),
  reason: z.string().trim().max(500).nullable().optional(),
});
export type PhoneLineStatusChangeInput = z.infer<typeof phoneLineStatusChangeSchema>;

export const phoneLineRotateSchema = z.object({
  /** The backup line to promote. Must belong to the same agent. */
  backupLineId: z.number().int().positive(),
  reason: z.string().trim().max(500).nullable().optional(),
});
export type PhoneLineRotateInput = z.infer<typeof phoneLineRotateSchema>;

export const reputationCheckWriteSchema = z.object({
  source: z.enum(REPUTATION_SOURCES),
  /**
   * Manual by default, and in practice always: nothing in this phase calls a
   * reputation API. The field exists so an automated check stays
   * distinguishable from someone reading a lookup page.
   */
  method: z.enum(REPUTATION_METHODS).default('manual'),
  labelObserved: z.string().trim().max(200).nullable().optional(),
  rawResponse: z.string().trim().max(10_000).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  /** Defaults to now. Present so a check made yesterday can be backdated. */
  checkedAt: z.number().int().positive().optional(),
});
export type ReputationCheckInput = z.infer<typeof reputationCheckWriteSchema>;

export const carrierTestWriteSchema = z.object({
  carrier: z.string().trim().min(1, 'Which carrier?').max(80),
  /**
   * A description of the handset, never its number. 'my personal mobile' is
   * the intended value; a phone number here would end up in every backup.
   */
  destinationLabel: z.string().trim().max(120).nullable().optional(),
  callerIdDisplay: z.string().trim().max(200).nullable().optional(),
  spamLabel: z.string().trim().max(120).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  testedAt: z.number().int().positive().optional(),
});
export type CarrierTestInput = z.infer<typeof carrierTestWriteSchema>;

export const phoneLineRollupSchema = z.object({
  /** Local calendar date to recompute. Defaults to today. */
  date: localDateSchema.optional(),
  /** How many days back from `date`, inclusive. Defaults to 1. */
  days: z.number().int().min(1).max(120).optional(),
});
export type PhoneLineRollupInput = z.infer<typeof phoneLineRollupSchema>;

// ---------------------------------------------------------------------------
// Phase 6 — user preferences
// ---------------------------------------------------------------------------

/**
 * The interface scale, validated against the four supported steps.
 *
 * `z.literal` per value rather than a numeric range: 105 is not a scale the
 * interface has ever been laid out against, and silently rounding it to the
 * nearest step would be worse than refusing it.
 */
export const uiScaleSchema = z.union([
  z.literal(100),
  z.literal(110),
  z.literal(125),
  z.literal(140),
]);

/**
 * A user's own preferences.
 *
 * Deliberately a named object rather than a free-form blob: every field here
 * has to be validated and rendered by something, and a bag of arbitrary keys
 * is a bag of arbitrary keys to sanitise later.
 */
export const userPreferencesSchema = z.object({
  uiScale: uiScaleSchema,
});
export type UserPreferences = z.infer<typeof userPreferencesSchema>;

/**
 * PATCH accepts any subset of the known fields, and at least one of them.
 *
 * `.strict()` matters here. Zod's default is to silently drop unknown keys, so
 * `{ uiScale: 110, isAdmin: true }` would have succeeded and the caller would
 * have had every reason to believe both were applied. This endpoint is
 * deliberately not a generic preference bag: an unrecognised field is a caller
 * bug or an attempt, and either deserves an answer rather than a shrug.
 */
export const userPreferencesPatchSchema = userPreferencesSchema
  .partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Nothing to change.',
  });
export type UserPreferencesPatch = z.infer<typeof userPreferencesPatchSchema>;
